// The staged world the theme preview draws (R806).
//
// It is a small world built from the wire's own types, so the real layers
// draw it and nothing here is a lookalike: a hello, the fleets of a state
// frame, one round in flight and the events of a fight. Every function is
// pure, so the tests read the fixture without a DOM or a GL context.
//
// The board is 18 × 12 cells. The viewer (player 7, faction 1) holds the
// west, a rival (player 9, faction 2) holds the east, and the water between
// them is open. A ridge stands in the north of the open water and a trench
// runs through the viewer's south.
//
// The staged world holds one of everything a theme field changes. The sites
// are the viewer's hold, a squad member's hold, the rival's hold, a stocked
// deposit, an empty one beside it, and two strategic objects the viewer's
// faction holds — a quiet Trench Throne and a Deep Battery under attack
// (R1103, R1104). The fleets are two own hulls with
// group badges 1 and 2, a Tender under way on its patient, a set Tender
// with its aura, an own hull and a squad member's hull inside that aura,
// two rival contacts in the east and one more beside the patrol.
//
// Every figure below is the fixture's own: a place on the board, a hull
// percentage, a loop length. None of them is a game rule, and the game
// reads none of them.

import {
  AnchorState, Event, EventKind, FleetState, Hello, MapMarker, MarkerAudience, MarkerKind,
  Mission, ObjKind, PointOfInterest, PoiKind, SeaObject, SeaObjectKind, type SeaObjectRules, type SeaObjectSpec, type SeaObjectStock,
  ShipClass, SiegeInfo, Site, SiteKind, TorpedoKind, TorpedoState,
} from '../gen/proto/aquaphobia';
import { REGION_NAMES } from '../gen/balance';
import { CAMERA_MAX_ZOOM, CAMERA_MIN_ZOOM } from '../gen/presentation';
import {
  BLOCK_RIDGE, BLOCK_TRENCH, COL_PITCH, HEX_SIZE, cellCenter, wireMpx,
} from '../world';
import type { PreviewEffects, PreviewFocus } from './look';

// ---------------------------------------------------------------- the board

export const PREVIEW_COLS = 18;
export const PREVIEW_ROWS = 12;
/** The board in world px: the server's `Grid.WorldPx` and `WorldHeightPx`. */
export const PREVIEW_W = PREVIEW_COLS * COL_PITCH;
export const PREVIEW_H = 1.5 * HEX_SIZE * (PREVIEW_ROWS - 1) + 2 * HEX_SIZE;

/** The viewer and the rival, as player ids and as factions. */
export const PREVIEW_ME = 7;
export const PREVIEW_RIVAL = 9;
export const PREVIEW_FACTION = 1;
export const PREVIEW_RIVAL_FACTION = 2;

/** A squad member of the viewer: another player of the viewer's faction. */
export const PREVIEW_MATE = 8;

/**
 * How far a set Tender's aura reaches, in world px.
 *
 * It is the fixture's own figure. The pack does not carry the sim's
 * `tender_anchor_radius_px` to the client, and the preview opens no socket,
 * so no hello of a live world reaches it. The layer draws no regen wave
 * while the reach is 0, so the hello below states one.
 */
export const PREVIEW_AURA_PX = 120;

/** The world clock the banner counts: twelve days of a season. */
export const PREVIEW_SEASON_LEFT_SEC = 12 * 86_400;
/** How long the whole season is, for the styles that draw it (R825). */
export const PREVIEW_SEASON_SEC = 28 * 86_400;

/** The cell index of a column and a row on the preview board. */
export function cellOf(col: number, row: number): number {
  return row * PREVIEW_COLS + col;
}

/** The centre of a preview cell in world px, on the preview grid. */
export function centreOf(cell: number): { x: number; y: number } {
  const [x, y] = cellCenter(cell, PREVIEW_COLS);
  return { x, y };
}

/** The ridge: rock in the north of the open water. */
export const RIDGE_CELLS: readonly number[] = [
  cellOf(8, 1), cellOf(9, 1), cellOf(10, 1), cellOf(8, 2), cellOf(9, 2), cellOf(9, 3),
];

/** The trench: deep water through the viewer's south. */
export const TRENCH_CELLS: readonly number[] = (() => {
  const out: number[] = [];
  for (let row = 8; row <= 10; row++) for (let col = 2; col <= 5; col++) out.push(cellOf(col, row));
  return out;
})();

export const HOLD_CELL = cellOf(3, 5);
export const MATE_HOLD_CELL = cellOf(2, 3);
export const RIVAL_HOLD_CELL = cellOf(14, 5);
export const DEPOSIT_CELL = cellOf(9, 8);
/** The empty deposit, beside the stocked one, so the two read together. */
export const EMPTY_DEPOSIT_CELL = cellOf(10, 8);
export const OWN_DEPOSIT_CELL = cellOf(5, 2);
/**
 * The two strategic objects, on open water south of the deposits (R1104).
 *
 * Both are held by the viewer's faction, so the ONLY thing that separates
 * the two marks is the attack pledge: the quiet one and the contested one
 * stand side by side, and one frame holds both.
 */
export const CP_QUIET_CELL = cellOf(8, 10);
export const CP_CONTESTED_CELL = cellOf(9, 10);

/**
 * The staged map markers (R1291), on the open water east of the ridge: the
 * three middle markers of the scene of mockup round 062. The top row is the
 * faction, a defend and a gather marker, and the bottom row is the squad,
 * one attack marker at half its life.
 */
export const MARKER_FACTION_CELLS: readonly number[] = [cellOf(12, 3), cellOf(14, 3)];
export const MARKER_SQUAD_CELLS: readonly number[] = [cellOf(13, 6)];
/** The age of the squad marker: half its life. */
export const MARKER_SQUAD_AGES: readonly number[] = [0.5];
/** The age of the faction row: young. */
export const MARKER_FACTION_AGE = 0.12;
/**
 * How long a staged marker lives, in wall seconds. It is the fixture's own
 * figure, as the aura reach is: the preview opens no socket, so the hello
 * below states the life the ages are measured against.
 */
export const PREVIEW_MARKER_LIFE_SEC = 1800;
/** The commanders of the staged markers, as the round's cards named them. */
const MARKER_NAMES = ['Haas', 'Moreau'] as const;
/** The viewer's own name, on the squad row. */
const MARKER_OWN_NAME = 'Vale';
const MARKER_KINDS = [MarkerKind.MARKER_KIND_DEFEND, MarkerKind.MARKER_KIND_GATHER] as const;

/**
 * The staged points of interest (R2714): one of each kind, round the
 * trench in the viewer's south, as the map view of mockup round 114 lays
 * them. The lair lies in the trench, the vent at its edge, the current
 * gate in the open water east of it, and the seeded cache and the ruin
 * beyond. The preview stages them only while the focus `poi` shows, so the
 * picture of every other row stays the picture it was.
 */
export const POI_CELLS: readonly { kind: PoiKind; cell: number }[] = [
  { kind: PoiKind.POI_KIND_LAIR, cell: cellOf(3, 9) },
  { kind: PoiKind.POI_KIND_VENT, cell: cellOf(5, 11) },
  { kind: PoiKind.POI_KIND_GATE, cell: cellOf(7, 10) },
  { kind: PoiKind.POI_KIND_CACHE, cell: cellOf(10, 11) },
  { kind: PoiKind.POI_KIND_RUIN, cell: cellOf(12, 10) },
];

/** The staged points of interest, as the survey store takes them. */
export function previewPois(): PointOfInterest[] {
  return POI_CELLS.map((p, i) => PointOfInterest.fromPartial({ id: 1 + i, kind: p.kind, cell: p.cell }));
}

/**
 * The staged markers at a wall second. The end of each life is stamped from
 * `nowSec`, so a preview that stays open shows the same ages each time it
 * shows the focus.
 */
export function previewMarkers(nowSec: number): MapMarker[] {
  const out: MapMarker[] = [];
  const end = (age: number) => Math.round(nowSec + PREVIEW_MARKER_LIFE_SEC * (1 - age));
  MARKER_FACTION_CELLS.forEach((cell, i) => out.push(MapMarker.fromPartial({
    id: 1 + i, ownerId: PREVIEW_MATE, ownerName: MARKER_NAMES[i], kind: MARKER_KINDS[i], cell,
    audience: MarkerAudience.MARKER_AUDIENCE_FACTION, expiresUnix: end(MARKER_FACTION_AGE),
  })));
  MARKER_SQUAD_CELLS.forEach((cell, i) => out.push(MapMarker.fromPartial({
    id: 11 + i, ownerId: PREVIEW_ME, ownerName: MARKER_OWN_NAME, kind: MarkerKind.MARKER_KIND_ATTACK,
    cell, audience: MarkerAudience.MARKER_AUDIENCE_SQUAD, expiresUnix: end(MARKER_SQUAD_AGES[i]),
  })));
  return out;
}

/** The two frontier cells the territory focus hands to the viewer and back. */
export const FLIP_CELLS: readonly number[] = [cellOf(8, 6), cellOf(8, 5)];

/** The owner byte of every cell: the viewer west, the rival east. */
export function previewOwners(): Uint8Array {
  const owners = new Uint8Array(PREVIEW_COLS * PREVIEW_ROWS);
  const rock = new Set(RIDGE_CELLS);
  for (let row = 0; row < PREVIEW_ROWS; row++) {
    for (let col = 0; col < PREVIEW_COLS; col++) {
      const i = cellOf(col, row);
      if (rock.has(i)) continue;
      if (col <= 6 + (row % 3 === 0 ? 1 : 0)) owners[i] = PREVIEW_FACTION;
      else if (col >= 11 - (row % 4 === 1 ? 1 : 0)) owners[i] = PREVIEW_RIVAL_FACTION;
    }
  }
  return owners;
}

/** The blocked mask: 0 open, 1 trench, 2 ridge. */
/**
 * The px a map hour of one unit of the staged current field (R2716). It is
 * the fixture's own figure, the median of the pack over its median units:
 * the preview opens no socket, so the hello states it.
 */
export const PREVIEW_CURRENT_UNIT_PX_H = 100 / 32;
/** The staged gyre: its centre cell, its radius in world px, its peak and
 *  the set to the east under it, in units of the field. */
const GYRE_CELL = cellOf(10, 6);
const GYRE_R_PX = 180;
const GYRE_UNITS = 70;
const SET_UNITS = 14;
const MAX_UNITS = 127;

/**
 * The staged current field (R2716), as `Hello.currents` carries it: one
 * int8 pair a cell. A gyre turns over the open water, a weak set runs east
 * under it, and the ridge runs none. Only the `currents` focus lays it on
 * the board (`preview.ts`), so every other row keeps its picture.
 */
export function previewCurrents(): Uint8Array {
  const out = new Uint8Array(2 * PREVIEW_COLS * PREVIEW_ROWS);
  const ridge = new Set(RIDGE_CELLS);
  const c0 = centreOf(GYRE_CELL);
  for (let c = 0; c < PREVIEW_COLS * PREVIEW_ROWS; c++) {
    if (ridge.has(c)) continue;
    const p = centreOf(c);
    const dx = p.x - c0.x, dy = p.y - c0.y, r = Math.hypot(dx, dy);
    const k = r / GYRE_R_PX, vt = r > 0 ? GYRE_UNITS * k * Math.exp(1 - k) : 0;
    let vx = SET_UNITS + (r > 0 ? (-dy / r) * vt : 0), vy = r > 0 ? (dx / r) * vt : 0;
    const s = Math.hypot(vx, vy);
    if (s > MAX_UNITS) { vx *= MAX_UNITS / s; vy *= MAX_UNITS / s; }
    out[2 * c] = Math.round(vx) & 0xff;
    out[2 * c + 1] = Math.round(vy) & 0xff;
  }
  return out;
}

/** The staged depth bands (R2726): each band reaches this many world px
 *  farther from the own hold than the band before it. */
const DEPTH_STEP_PX = 110;

/**
 * The staged band field (R2726), as `Hello.depth` carries it: one band a
 * cell. The shelf lies round the viewer's own hold, and the water deepens
 * with the distance from it, through the slope and the deep to the abyss,
 * so one frame holds every band edge and the own hulls stand near them.
 * The ridge has no band. Only the `depth` focus lays it on the board
 * (`preview.ts`), so every other row keeps its picture.
 */
export function previewDepth(): Uint8Array {
  const out = new Uint8Array(PREVIEW_COLS * PREVIEW_ROWS);
  const ridge = new Set(RIDGE_CELLS);
  const h = centreOf(HOLD_CELL);
  for (let c = 0; c < out.length; c++) {
    if (ridge.has(c)) continue;
    const p = centreOf(c);
    out[c] = Math.min(4, 1 + Math.floor(Math.hypot(p.x - h.x, p.y - h.y) / DEPTH_STEP_PX));
  }
  return out;
}

export function previewBlocked(): Uint8Array {
  const blocked = new Uint8Array(PREVIEW_COLS * PREVIEW_ROWS);
  for (const i of RIDGE_CELLS) blocked[i] = BLOCK_RIDGE;
  for (const i of TRENCH_CELLS) blocked[i] = BLOCK_TRENCH;
  return blocked;
}

/** The site id of the viewer's own hold. It is the one site that wears an
 *  effect, because the hold slot is the player's own hold (R910). */
export const OWN_HOLD_SITE = 1;

/** The site ids of the two strategic objects (R1104). */
export const QUIET_CP_SITE = 7;
export const CONTESTED_CP_SITE = 8;

/**
 * The eight sites: three holds of three ranks, three deposits and two
 * strategic objects.
 *
 * The holds carry the three ranks a hold mark can wear — the viewer's own,
 * a squad member's and a foreign one — so a mark row shows what it changes.
 * The deposit beside the stocked one is empty, so a deposit icon shows both
 * ends of its gauge in one picture.
 *
 * The two control points do the same for the structure symbols (R1103): the
 * viewer's faction holds both, one stands quiet and one is under attack, so
 * a press on the STRUCTURE ICON row shows the set's mark and its contested
 * tell side by side. A `Site` record carries the SITE kind alone, so the
 * OBJECT kind of each one comes from `previewSieges`.
 *
 * `effects` puts the hold pick on the viewer's own hold. Every other site
 * wears no effect: a foreign hold is another account's pick.
 */
export function previewSites(effects?: PreviewEffects): Site[] {
  return [
    Site.fromPartial({
      id: OWN_HOLD_SITE, kind: SiteKind.SITE_KIND_PLAYER_BASE, cell: HOLD_CELL,
      faction: PREVIEW_FACTION, ownerPlayer: PREVIEW_ME, effect: effects?.hold ?? 0,
    }),
    Site.fromPartial({
      id: 2, kind: SiteKind.SITE_KIND_PLAYER_BASE, cell: RIVAL_HOLD_CELL,
      faction: PREVIEW_RIVAL_FACTION, ownerPlayer: PREVIEW_RIVAL,
    }),
    Site.fromPartial({
      id: DEPOSIT_SITE, kind: SiteKind.SITE_KIND_DEPOSIT, cell: DEPOSIT_CELL,
      resource: DEPOSIT_RESOURCE, amount: 420, capacity: 1000,
    }),
    Site.fromPartial({
      id: 4, kind: SiteKind.SITE_KIND_DEPOSIT, cell: OWN_DEPOSIT_CELL,
      faction: PREVIEW_FACTION, resource: 3, amount: 900, capacity: 1000,
    }),
    Site.fromPartial({
      id: 5, kind: SiteKind.SITE_KIND_PLAYER_BASE, cell: MATE_HOLD_CELL,
      faction: PREVIEW_FACTION, ownerPlayer: PREVIEW_MATE,
    }),
    Site.fromPartial({
      id: 6, kind: SiteKind.SITE_KIND_DEPOSIT, cell: EMPTY_DEPOSIT_CELL,
      resource: 1, amount: 0, capacity: 1000,
    }),
    Site.fromPartial({
      id: QUIET_CP_SITE, kind: SiteKind.SITE_KIND_CONTROL_POINT,
      cell: CP_QUIET_CELL, faction: PREVIEW_FACTION,
    }),
    Site.fromPartial({
      id: CONTESTED_CP_SITE, kind: SiteKind.SITE_KIND_CONTROL_POINT,
      cell: CP_CONTESTED_CELL, faction: PREVIEW_FACTION,
    }),
  ];
}

/**
 * The Sieges frame of the staged world (R1104).
 *
 * The frame is the one place the client learns what a strategic object IS,
 * so the preview must send it: without it the site layer keys both control
 * points at the unspecified kind and no structure symbol draws. The quiet
 * object is a Trench Throne and the contested one a Deep Battery, and the
 * rival faction is the one that pledged.
 *
 * The window figures below are the fixture's own. The preview opens no
 * panel that reads a window, and the map draws none of them.
 */
export function previewSieges(): SiegeInfo[] {
  return [
    SiegeInfo.fromPartial({
      siteId: QUIET_CP_SITE, cell: CP_QUIET_CELL, owner: PREVIEW_FACTION,
      kind: ObjKind.OBJ_KIND_THRONE, gauge10: 1000,
    }),
    SiegeInfo.fromPartial({
      siteId: CONTESTED_CP_SITE, cell: CP_CONTESTED_CELL,
      owner: PREVIEW_FACTION, kind: ObjKind.OBJ_KIND_BATTERY,
      contested: true, attacker: PREVIEW_RIVAL_FACTION, gauge10: 640,
    }),
  ];
}

/**
 * The hello of the staged world. The world name is a region of the pack.
 *
 * The catalog carries the aura reach the fleet layer needs to draw a regen
 * wave (R723), the two anchor figures the anchoring look reads (R1125) and
 * the life the staged map markers are aged against (R1291), and the
 * figures the marks of the staged sea objects draw to scale (R2659).
 * Every other table stays out, because the preview opens no panel that
 * reads one.
 */
export function previewHello(effects?: PreviewEffects): Hello {
  return Hello.fromPartial({
    catalog: {
      rules: {
        tenderAuraPx: PREVIEW_AURA_PX, markerLifeSec: PREVIEW_MARKER_LIFE_SEC, seaObjects: PREVIEW_SEA_RULES,
        currentUnitPxH: PREVIEW_CURRENT_UNIT_PX_H,
      },
      seaObjects: PREVIEW_SEA_KINDS,
      anchorCycleSec: PREVIEW_ANCHOR_CYCLE_SEC,
      beaconBeamSec: PREVIEW_BEAM_SEC,
    },
    cols: PREVIEW_COLS,
    rows: PREVIEW_ROWS,
    faction: PREVIEW_FACTION,
    owners: previewOwners(),
    blocked: previewBlocked(),
    sites: previewSites(effects),
    playerId: PREVIEW_ME,
    homeCell: HOLD_CELL,
    season: 1,
    worldName: String(REGION_NAMES[0] ?? ''),
    timescale: 1,
    tacticalScale: 1,
  });
}

/**
 * The figures of the staged sea objects (R2659): the pack's own figures of
 * `seaobjects.toml` and `tech.toml`, as the hello of a world with `sapper`
 * latched carries them. The preview opens no socket, so the hello states
 * them, and `fixture.test.ts` holds them to `balance.json`.
 */
export const PREVIEW_SEA_RULES: Partial<SeaObjectRules> = {
  armClearPx: 40, layNoiseMul: 1.5, layNoiseSec: 120, collectPx: 25,
  sweepPx: 150, sweepRadiusMul: 1.5, anchorSec: 60, palisadeAnchorSec: 120, armAfterSec: 150,
  laneChainPx: 150, laneSnapPx: 40,
};

/** One staged kind: the figures a mark reads, every other one 0. */
function seaKind(id: SeaObjectKind, cargo: number, f: Partial<SeaObjectSpec>): Partial<SeaObjectSpec> {
  return { id, seaMine: id <= SeaObjectKind.SEA_OBJECT_KIND_PETARD, cargo, ...f };
}

/** The ten kinds, in the order of the wire enum. */
export const PREVIEW_SEA_KINDS: Partial<SeaObjectSpec>[] = [
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_BARB, 10, { triggerPx: 12, damage: 80 }),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_BOLA, 10, { triggerPx: 12, damage: 15 }),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_SIREN, 15, { triggerPx: 12, hearPx: 50, splashPx: 15, damage: 100 }),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_PETARD, 25, { triggerPx: 15, splashPx: 20, damage: 220 }),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_SEINE, 30, { lengthPx: 80 }),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_CHEVAL, 25, { lengthPx: 30 }),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_PICKET_BUOY, 20, { sonarPx: 200 }),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT, 15, { reachPx: 80 }),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_DECOY_BUOY, 10, {}),
  seaKind(SeaObjectKind.SEA_OBJECT_KIND_PALISADE, 60, { lengthPx: 60 }),
];

// --------------------------------------------------------------- the fleets

/**
 * The twelve fleets' ids. An own fleet counts from 101 and a foreign one from
 * 201. The squad member's hull is foreign to the viewer, so it counts with
 * the contacts.
 *
 * The two own ones that hold groups 1 and 2 are the patrol and the gunner.
 */
export const FLEET_PATROL = 101;
export const FLEET_GUNNER = 102;
/** The Tender under way. It tends the patrol, so the heal beam draws. */
export const FLEET_TENDER = 103;
/** The Tender with its anchor down. It throws the aura. */
export const FLEET_ANCHOR = 104;
/** An own hull inside the aura. It holds fire, so the hold-fire look
 *  draws round it (R1439). */
export const FLEET_WARD = 105;
/** The Dredger at work on the stocked deposit (R1120). It stands ON the
 *  field, because a Dredger cuts from inside `action_radius` (R80), and it
 *  carries MISSION_GATHER and a part-filled hold, which is what the gather
 *  tracker reads (R1121). */
export const FLEET_DREDGER = 106;
/** The own Beacon whose cable runs the whole anchor cycle (R1125): SETTING,
 *  SET with its beam order running (R2858), and WEIGHING, one `ANCHOR_STEP_SEC` each. */
export const FLEET_BEACON = 107;
/** The own Lancer whose tubes fire, reload and wait, over and over, so the
 *  tube reload look shows the shot, the reload and the ready moment (R1474).
 *  It sails with Remoras, the card's selected Lancer. */
export const FLEET_TUBES = 108;

/**
 * The staged tubes of the Lancer (R1474). The preview opens no socket and
 * its hello carries no ship table, because a ship table would take the
 * shot looks' own roster away, so the preview hands the fleet layer these
 * tubes itself. The magazine is the pack's: a Lancer's 60 cargo over 5 a
 * light round (numbers.md §5). The name is the catalog's name of the
 * seeker round.
 */
export const PREVIEW_MAGAZINE = 12;
export const PREVIEW_ROUND_KIND = TorpedoKind.TORPEDO_KIND_SEEKER;
export const PREVIEW_ROUND_NAME = 'REMORA';
/** The rounds aboard: the card's selected Lancer, 7 of 12. */
export const PREVIEW_ROUNDS = 7;
/** One lap of the staged tubes, in map seconds, which the preview runs at
 *  one a wall second: the shot, a reload of `PREVIEW_RELOAD_SEC`, then a
 *  wait. It is the card's own lap of 20 and 10. */
export const PREVIEW_RELOAD_SEC = 20;
export const PREVIEW_TUBE_LAP_SEC = 30;

/** The tube fields of the staged Lancer at `tSec` (R1474). The tube fires
 *  at the head of each lap. */
export function tubesAt(tSec: number): Partial<FleetState> {
  const into = ((tSec % PREVIEW_TUBE_LAP_SEC) + PREVIEW_TUBE_LAP_SEC) % PREVIEW_TUBE_LAP_SEC;
  return {
    // The load by kind and the active round (R2017).
    rounds: [{ kind: PREVIEW_ROUND_KIND, count: PREVIEW_ROUNDS }],
    activeRound: PREVIEW_ROUND_KIND,
    reloadFullSec: PREVIEW_RELOAD_SEC,
    reloadSec: into < PREVIEW_RELOAD_SEC ? Math.floor(PREVIEW_RELOAD_SEC - into) : 0,
  };
}

/** The anchor cycle and the beam order of the staged world, in map seconds.
 *  They are the pack's own figures (numbers.md §16); the preview opens no
 *  socket, so the hello states them. */
export const PREVIEW_ANCHOR_CYCLE_SEC = 600;
export const PREVIEW_BEAM_SEC = 54000;
/** How long the staged Beacon holds each of the three states, in seconds. */
export const ANCHOR_STEP_SEC = 6;
/** The share of the beam order that runs out over one SET step: the card's
 *  own `1 - 0.35 u` of round 056. */
const ANCHOR_LIFT_RUN = 0.35;

/** The anchor fields of the staged Beacon at `tSec` (R1125). */
export function beaconAnchorAt(tSec: number): Partial<FleetState> {
  const step = Math.floor(tSec / ANCHOR_STEP_SEC) % 3;
  const u = (tSec % ANCHOR_STEP_SEC) / ANCHOR_STEP_SEC;
  if (step === 1) {
    return {
      anchor: AnchorState.ANCHOR_STATE_SET,
      beamLeftSec: Math.round(PREVIEW_BEAM_SEC * (1 - ANCHOR_LIFT_RUN * u)),
    };
  }
  return {
    anchor: step === 0 ? AnchorState.ANCHOR_STATE_SETTING : AnchorState.ANCHOR_STATE_WEIGHING,
    anchorLeftSec: Math.round(PREVIEW_ANCHOR_CYCLE_SEC * (1 - u)),
  };
}

/** The stocked deposit the Dredger works, and the commodity it holds. */
export const DEPOSIT_SITE = 3;
export const DEPOSIT_RESOURCE = 1;
/** What the staged Dredger's hold carries, and what it takes (R1120). The
 *  hold is a third full, so a gauge has a figure that is neither end. */
export const PREVIEW_HOLD = 40;
export const PREVIEW_CARGO_CAP = 120;
export const FLEET_TARGET = 201;
export const FLEET_SPLASH = 202;
/** A contact beside the patrol, so one picture holds two shield readouts. */
export const FLEET_CONTACT = 203;
/** The squad member's hull, inside the aura. */
export const FLEET_MATE = 204;

/** Where each fleet stands at rest, in world px. */
const REST: Readonly<Record<number, { x: number; y: number }>> = {
  [FLEET_PATROL]: centreOf(cellOf(7, 7)),
  [FLEET_GUNNER]: centreOf(cellOf(6, 4)),
  [FLEET_TENDER]: centreOf(cellOf(6, 8)),
  [FLEET_ANCHOR]: centreOf(cellOf(4, 6)),
  [FLEET_WARD]: centreOf(cellOf(4, 5)),
  // Two px short of the field's own centre, the rest place of the mockup
  // stage: the hull lies ON the diamond.
  [FLEET_DREDGER]: { x: centreOf(DEPOSIT_CELL).x - 2, y: centreOf(DEPOSIT_CELL).y },
  [FLEET_BEACON]: centreOf(cellOf(10, 5)),
  [FLEET_TUBES]: centreOf(cellOf(6, 2)),
  [FLEET_TARGET]: centreOf(cellOf(12, 4)),
  [FLEET_SPLASH]: centreOf(cellOf(12, 7)),
  [FLEET_CONTACT]: centreOf(cellOf(9, 7)),
  [FLEET_MATE]: centreOf(cellOf(5, 7)),
};

/** The patrol's circle: its radius in world px and its period in seconds. */
const PATROL_R = 16;
const PATROL_SEC = 12;

/** The control groups the two own fleets hold (R403). */
export const PREVIEW_GROUPS: ReadonlyMap<number, number> = new Map([
  [FLEET_PATROL, 1], [FLEET_GUNNER, 2],
]);

/** Where a fleet is at `tSec`. Only the patrol moves. */
export function fleetAt(id: number, tSec: number): { x: number; y: number } {
  const r = REST[id] ?? { x: 0, y: 0 };
  if (id !== FLEET_PATROL) return r;
  const a = (tSec / PATROL_SEC) * Math.PI * 2;
  return { x: r.x + PATROL_R * Math.cos(a), y: r.y + PATROL_R * Math.sin(a) };
}

function fleet(
  id: number, tSec: number, cls: ShipClass, own: boolean, hullPct: number, shieldPct: number,
  over: Partial<FleetState> = {},
): FleetState {
  const at = fleetAt(id, tSec);
  const ahead = fleetAt(id, tSec + 1);
  return FleetState.fromPartial({
    id,
    faction: own ? PREVIEW_FACTION : PREVIEW_RIVAL_FACTION,
    ownerPlayer: own ? PREVIEW_ME : PREVIEW_RIVAL,
    xMpx: wireMpx(at.x),
    yMpx: wireMpx(at.y),
    txMpx: wireMpx(ahead.x),
    tyMpx: wireMpx(ahead.y),
    cls, own, hullPct, shieldPct, weapType: 1, shieldType: 1,
    ...over,
  });
}

/**
 * The twelve fleets at `tSec`, as the fleets of a state frame.
 *
 * No hull is whole, so a bar shows on every one of them. The Tender under
 * way names the patrol as its patient, and the two hulls inside the aura
 * name the set Tender, which is how the layer finds both marks (R725).
 *
 * `effects` puts the fleet pick on the patrol alone (R910). One marked hull
 * beside eight plain ones is what a reader compares, and the patrol is the
 * hull the effect focus looks at.
 */
export function previewFleets(tSec: number, effects?: PreviewEffects): FleetState[] {
  return [
    fleet(FLEET_PATROL, tSec, ShipClass.SHIP_CLASS_CUTLASS, true, 78, 55, {
      effect: effects?.fleet ?? 0,
    }),
    fleet(FLEET_GUNNER, tSec, ShipClass.SHIP_CLASS_CORSAIR, true, 92, 70),
    fleet(FLEET_TENDER, tSec, ShipClass.SHIP_CLASS_TENDER, true, 88, 60, {
      tending: FLEET_PATROL,
    }),
    fleet(FLEET_ANCHOR, tSec, ShipClass.SHIP_CLASS_TENDER, true, 95, 80, {
      anchor: AnchorState.ANCHOR_STATE_SET, aura: FLEET_ANCHOR,
    }),
    fleet(FLEET_WARD, tSec, ShipClass.SHIP_CLASS_SENTINEL, true, 72, 20, {
      aura: FLEET_ANCHOR,
      holdFire: true,
    }),
    // The Dredger works the stocked deposit (R1120). The hold is part full
    // and the cargo cap is over it, so a look that gauges the hold has a
    // figure to draw; the mission is what the gather tracker reads (R1121).
    fleet(FLEET_DREDGER, tSec, ShipClass.SHIP_CLASS_DREDGER, true, 96, 90, {
      mission: Mission.MISSION_GATHER,
      hold: [{ resource: DEPOSIT_RESOURCE, amount: PREVIEW_HOLD }],
      cargoCap: PREVIEW_CARGO_CAP,
    }),
    // The Beacon runs the anchor cycle, so the anchoring look shows all three
    // states on its own hull (R1125).
    fleet(FLEET_BEACON, tSec, ShipClass.SHIP_CLASS_BEACON, true, 90, 75,
      beaconAnchorAt(tSec)),
    // The Lancer fires, reloads and waits, so the tube reload look shows
    // every state but the empty tubes on its own hull (R1474).
    fleet(FLEET_TUBES, tSec, ShipClass.SHIP_CLASS_LANCER, true, 94, 85, tubesAt(tSec)),
    fleet(FLEET_TARGET, tSec, ShipClass.SHIP_CLASS_BASILISK, false, 64, 35),
    fleet(FLEET_SPLASH, tSec, ShipClass.SHIP_CLASS_FALCONET, false, 81, 48),
    fleet(FLEET_CONTACT, tSec, ShipClass.SHIP_CLASS_VEDETTE, false, 70, 45),
    fleet(FLEET_MATE, tSec, ShipClass.SHIP_CLASS_LANCER, false, 84, 40, {
      faction: PREVIEW_FACTION, ownerPlayer: PREVIEW_MATE, aura: FLEET_ANCHOR,
    }),
  ];
}

// ------------------------------------------------------------ the Sapper part

/**
 * The staged own Sapper of the Sapper part (R2652, R2657). It stands on the
 * board only while a row of the part shows, so the picture of every other
 * row stays the picture it was.
 */
export const FLEET_SAPPER = 109;
/** Where the Sapper stands: south-east open water, clear of every other
 *  staged thing. */
const SAPPER_AT = centreOf(cellOf(14, 9));

/** The seven foci of the Sapper part: the marks and the work looks. */
export const SAPPER_FOCI: ReadonlySet<PreviewFocus> = new Set<PreviewFocus>([
  'seaMine', 'deployable', 'seaMineDetonation', 'seaMineLay', 'deployableLay', 'seaMineCollect',
  'deployableCollect',
]);

/** One loop of a work look, in seconds: the lay, the arm or the anchor,
 *  the take, and a rest. */
export const SEA_LOOP_SEC = 7;
/** The beat of a loop the lay, the take or the detonation lands on. */
const SEA_BEAT_SEC = 1;
/** The beat the laid object arms or anchors on. */
const SEA_ARM_SEC = 3;
/** The loud lay of the stage, in map seconds: the preview runs one map
 *  second a wall second. */
const SEA_LOUD_SEC = 5;

/** The sea object ids of the stage. */
const SO_BASE = 300;

/** A staged sea object at `dx, dy` from the Sapper. */
function seaRow(id: number, kind: SeaObjectKind, dx: number, dy: number, over: Partial<SeaObject> = {},
  at: { x: number; y: number } = SAPPER_AT): SeaObject {
  return SeaObject.fromPartial({
    id: SO_BASE + id, faction: PREVIEW_FACTION, own: true, kind,
    xMpx: wireMpx(at.x + dx), yMpx: wireMpx(at.y + dy), hullPct: 100, armed: true, ...over,
  });
}

/** What the stage holds at one moment of one focus. */
export interface SeaStage {
  /** The rows of the picture now. */
  rows: SeaObject[];
  /** The stow and the loud lay of the Sapper now. */
  stow: SeaObjectStock[];
  loudLeftSec: number;
  /** True on the first frame of a loop: the frame restates the picture, so
   *  the store reads no lay off the rows that come back. */
  full: boolean;
}

/**
 * The stage of one focus of the Sapper part at `tSec` (R2657). The store
 * of the client reads the lay, the collect and the detonation off the
 * rows (`seaobjects.ts`), so the stage drives each look with rows alone:
 * a row that appears beside the Sapper is a lay; rows that leave on the
 * frame the stow grows are a collect; an armed sea mine that leaves under a
 * hull is a detonation.
 */
export function seaStage(focus: PreviewFocus, tSec: number): SeaStage {
  const k = ((tSec % SEA_LOOP_SEC) + SEA_LOOP_SEC) % SEA_LOOP_SEC;
  const loop = Math.floor(Math.max(0, tSec) / SEA_LOOP_SEC);
  const out: SeaStage = { rows: [], stow: [], loudLeftSec: 0, full: k < 0.15 };
  const K = SeaObjectKind;
  switch (focus) {
    case 'seaMine':
      // The four kinds, own and armed, an own one unarmed, and a hostile one.
      out.rows = [
        seaRow(1, K.SEA_OBJECT_KIND_BARB, -40, -18), seaRow(2, K.SEA_OBJECT_KIND_BOLA, -20, -30),
        seaRow(3, K.SEA_OBJECT_KIND_SIREN, 0, -38), seaRow(4, K.SEA_OBJECT_KIND_PETARD, 22, -30),
        seaRow(5, K.SEA_OBJECT_KIND_BARB, -42, 16, { armed: false }),
        seaRow(6, K.SEA_OBJECT_KIND_BARB, 36, 20, { faction: PREVIEW_RIVAL_FACTION, own: false }),
      ];
      break;
    case 'deployable':
      out.rows = [
        seaRow(1, K.SEA_OBJECT_KIND_SEINE, -36, -30, { headingMrad: 300 }),
        seaRow(2, K.SEA_OBJECT_KIND_CHEVAL, 36, -26),
        seaRow(3, K.SEA_OBJECT_KIND_LANE_LIGHT, -38, 26),
        seaRow(4, K.SEA_OBJECT_KIND_PALISADE, 34, 30, { headingMrad: -400, hullPct: 70 }),
      ];
      break;
    case 'seaMineLay':
    case 'deployableLay': {
      // One lay a loop, astern of the Sapper, with a new id each loop so the
      // store reads a new row; it arms or anchors two seconds on.
      const mine = focus === 'seaMineLay';
      if (k >= SEA_BEAT_SEC) {
        out.rows = [seaRow(10 + loop, mine ? K.SEA_OBJECT_KIND_BARB : K.SEA_OBJECT_KIND_SEINE,
          mine ? -26 : -32, 0, { armed: k >= SEA_ARM_SEC, headingMrad: 0 })];
        out.loudLeftSec = Math.max(0, Math.ceil(SEA_LOUD_SEC - (k - SEA_BEAT_SEC)));
      }
      out.full = false;
      break;
    }
    case 'seaMineCollect':
    case 'deployableCollect': {
      // The field lies round the Sapper until the beat; then it leaves the
      // picture on the frame the stow grows, which is the take.
      const field = focus === 'seaMineCollect'
        ? [seaRow(20, K.SEA_OBJECT_KIND_BARB, -14, 8), seaRow(21, K.SEA_OBJECT_KIND_BOLA, 12, -10),
          seaRow(22, K.SEA_OBJECT_KIND_PETARD, 6, 16, { faction: PREVIEW_RIVAL_FACTION, own: false })]
        : [seaRow(20, K.SEA_OBJECT_KIND_LANE_LIGHT, -16, 6), seaRow(21, K.SEA_OBJECT_KIND_CHEVAL, 14, -12)];
      if (k < SEA_BEAT_SEC + 0.5) out.rows = field;
      else out.stow = field.map((r) => ({ kind: r.kind, count: 1 }));
      break;
    }
    case 'seaMineDetonation': {
      // An own Barb lies on the rival target, and leaves the picture under
      // it at the beat: the store reads that as the detonation.
      const at = fleetAt(FLEET_TARGET, 0);
      if (k < SEA_BEAT_SEC + 0.5) out.rows = [seaRow(30, K.SEA_OBJECT_KIND_BARB, 6, 0, {}, at)];
      break;
    }
    default:
      break;
  }
  return out;
}

/** The staged Sapper at `tSec`, with the stow and the loud lay of the stage. */
export function sapperFleet(stage: SeaStage): FleetState {
  return FleetState.fromPartial({
    id: FLEET_SAPPER, faction: PREVIEW_FACTION, ownerPlayer: PREVIEW_ME,
    xMpx: wireMpx(SAPPER_AT.x), yMpx: wireMpx(SAPPER_AT.y), txMpx: wireMpx(SAPPER_AT.x), tyMpx: wireMpx(SAPPER_AT.y),
    cls: ShipClass.SHIP_CLASS_SAPPER, own: true, hullPct: 90, shieldPct: 70, weapType: 1, shieldType: 1,
    stow: stage.stow, loudLeftSec: stage.loudLeftSec, cargoCap: PREVIEW_CARGO_CAP,
  });
}

/** Where the Sapper stands, for the frame of a focus. */
export function sapperAt(): { x: number; y: number } { return { ...SAPPER_AT }; }

// ------------------------------------------------------- the cache work look

/**
 * The staged own Vedette of the cache work look (R2833). It stands on the
 * board only while the CACHES row shows, at the place of the Sapper, which
 * stands there only for the rows of the Sapper part.
 */
export const FLEET_SEEKER = 110;
/** One loop of the work, in map seconds: the preview runs one map second
 *  a wall second. The fleet works for `CACHE_WORK_SEC` and rests for
 *  `CACHE_REST_SEC`, so the look plays all its takes and then stops. */
export const CACHE_WORK_SEC = 15;
const CACHE_REST_SEC = 2;

/** The map seconds the staged SEARCH still works at `tSec`, rounded up
 *  as the wire rounds them; 0 while it rests. */
export function cacheWorkLeft(tSec: number): number {
  const loop = CACHE_WORK_SEC + CACHE_REST_SEC;
  const k = ((tSec % loop) + loop) % loop;
  return k < CACHE_WORK_SEC ? Math.ceil(CACHE_WORK_SEC - k) : 0;
}

/** The staged Vedette at `tSec`: a SEARCH at its own point. */
export function seekerFleet(tSec: number): FleetState {
  return FleetState.fromPartial({
    id: FLEET_SEEKER, faction: PREVIEW_FACTION, ownerPlayer: PREVIEW_ME,
    xMpx: wireMpx(SAPPER_AT.x), yMpx: wireMpx(SAPPER_AT.y), txMpx: wireMpx(SAPPER_AT.x), tyMpx: wireMpx(SAPPER_AT.y),
    cls: ShipClass.SHIP_CLASS_VEDETTE, own: true, hullPct: 90, shieldPct: 70, weapType: 1, shieldType: 1,
    mission: Mission.MISSION_SEARCH, workLeftSec: cacheWorkLeft(tSec),
    workFullSec: cacheWorkLeft(tSec) > 0 ? CACHE_WORK_SEC : 0,
  });
}

// ------------------------------------------------------------ the torpedo run

/** One run: the flight, then a rest before the next launch. */
export const TORPEDO_FLIGHT_SEC = 2.4;
export const TORPEDO_REST_SEC = 1.2;
export const TORPEDO_LOOP_SEC = TORPEDO_FLIGHT_SEC + TORPEDO_REST_SEC;

/** Which run `tSec` is in, from 0. */
export function torpedoCycle(tSec: number): number {
  return Math.floor(Math.max(0, tSec) / TORPEDO_LOOP_SEC);
}

/** The rounds the detonation row runs, one a run, in turn (R2079): the
 *  three rounds the cards of round 065 show, of splash 20, 10 and 0. */
export const DETONATION_KINDS: readonly TorpedoKind[] = [
  TorpedoKind.TORPEDO_KIND_HEAVY, TorpedoKind.TORPEDO_KIND_LIGHT, TorpedoKind.TORPEDO_KIND_DISRUPTOR,
];

/** The round of the run `tSec` is in, on the detonation row. */
export function detonationKindAt(tSec: number): TorpedoKind {
  return DETONATION_KINDS[torpedoCycle(tSec) % DETONATION_KINDS.length];
}

/**
 * The round in flight at `tSec`, or null while the run rests.
 *
 * The gunner fires at the target. The round's id is new every run, so a run
 * never inherits the shed marks of the one before it. `effects` puts the
 * torpedo pick on the round (R910).
 */
export function torpedoAt(
  tSec: number, effects?: PreviewEffects, kind: TorpedoKind = TorpedoKind.TORPEDO_KIND_HEAVY,
): TorpedoState | null {
  const t = Math.max(0, tSec);
  const phase = t - torpedoCycle(t) * TORPEDO_LOOP_SEC;
  if (phase >= TORPEDO_FLIGHT_SEC) return null;
  const u = phase / TORPEDO_FLIGHT_SEC;
  const a = fleetAt(FLEET_GUNNER, t), b = fleetAt(FLEET_TARGET, t);
  const x = a.x + (b.x - a.x) * u, y = a.y + (b.y - a.y) * u;
  return TorpedoState.fromPartial({
    id: 1000 + torpedoCycle(t),
    faction: PREVIEW_FACTION,
    kind,
    xMpx: wireMpx(x),
    yMpx: wireMpx(y),
    headingMrad: Math.round(Math.atan2(b.y - a.y, b.x - a.x) * 1000),
    runPct: Math.round(u * 100),
    effect: effects?.torpedo ?? 0,
  });
}

/** One hit on a victim, as the wire sends it. */
function hit(
  kind: EventKind, victim: number, tSec: number, damage: number,
  taken: boolean, splash = false, source = FLEET_GUNNER,
): Event {
  const at = fleetAt(victim, tSec), from = fleetAt(source, tSec);
  return Event.fromPartial({
    kind,
    xMpx: wireMpx(at.x),
    yMpx: wireMpx(at.y),
    faction: taken ? PREVIEW_RIVAL_FACTION : PREVIEW_FACTION,
    actor: taken ? PREVIEW_RIVAL : PREVIEW_ME,
    subject: taken ? PREVIEW_ME : PREVIEW_RIVAL,
    damageX10: Math.round(damage * 10),
    victim,
    splash,
    sourceXMpx: wireMpx(from.x),
    sourceYMpx: wireMpx(from.y),
    victimFaction: taken ? PREVIEW_FACTION : PREVIEW_RIVAL_FACTION,
  });
}

/** The detonation that ends a run: the viewer's round on the target. It
 *  names the kind of its round, as the wire does (R2079). */
export function torpedoHit(tSec: number, kind: TorpedoKind = TorpedoKind.TORPEDO_KIND_HEAVY): Event {
  return { ...hit(EventKind.EVENT_KIND_TORPEDO_HIT, FLEET_TARGET, tSec, 48, false), torpedoKind: kind };
}

// ------------------------------------------------------------ the damage loop

/** One beat of the damage loop. */
interface Beat {
  at: number;
  kind: EventKind;
  victim: number;
  damage: number;
  taken: boolean;
  splash?: boolean;
  source: number;
}

/** The damage loop: gunfire dealt and taken, a torpedo hit and its splash. */
export const DAMAGE_LOOP_SEC = 3.2;
const BEATS: readonly Beat[] = [
  { at: 0.2, kind: EventKind.EVENT_KIND_HIT, victim: FLEET_TARGET, damage: 12.5, taken: false, source: FLEET_GUNNER },
  { at: 1.0, kind: EventKind.EVENT_KIND_HIT, victim: FLEET_PATROL, damage: 8, taken: true, source: FLEET_SPLASH },
  { at: 1.8, kind: EventKind.EVENT_KIND_TORPEDO_HIT, victim: FLEET_TARGET, damage: 46, taken: false, source: FLEET_GUNNER },
  { at: 1.8, kind: EventKind.EVENT_KIND_TORPEDO_HIT, victim: FLEET_SPLASH, damage: 9, taken: false, splash: true, source: FLEET_GUNNER },
  { at: 2.6, kind: EventKind.EVENT_KIND_HIT, victim: FLEET_SPLASH, damage: 6, taken: false, source: FLEET_PATROL },
];

/** Every event of the damage loop whose time falls in `(t0, t1]`. */
export function damageEvents(t0: number, t1: number): Event[] {
  const out: Event[] = [];
  if (!(t1 > t0)) return out;
  const c0 = Math.floor(Math.max(0, t0) / DAMAGE_LOOP_SEC);
  const c1 = Math.floor(Math.max(0, t1) / DAMAGE_LOOP_SEC);
  for (let c = c0; c <= c1; c++) {
    for (const b of BEATS) {
      const t = c * DAMAGE_LOOP_SEC + b.at;
      if (t > t0 && t <= t1) out.push(hit(b.kind, b.victim, t, b.damage, b.taken, b.splash, b.source));
    }
  }
  return out;
}

/** The territory loop: the two frontier cells change hands this often. */
export const FLIP_SEC = 2.5;

/** The owner of the frontier cells at `tSec`: open water, then the viewer's. */
export function flipOwner(tSec: number): number {
  return Math.floor(Math.max(0, tSec) / FLIP_SEC) % 2 === 0 ? 0 : PREVIEW_FACTION;
}

// --------------------------------------------------------------- the camera

/** What the camera must show: a centre and the extent around it, world px. */
export interface FocusTarget {
  x: number;
  y: number;
  spanX: number;
  spanY: number;
}

/** The whole board with a small margin. */
const WHOLE: FocusTarget = {
  x: PREVIEW_W / 2, y: PREVIEW_H / 2, spanX: PREVIEW_W * 1.04, spanY: PREVIEW_H * 1.04,
};

function around(p: { x: number; y: number }, spanX: number, spanY: number): FocusTarget {
  return { x: p.x, y: p.y, spanX, spanY };
}

/** The point halfway between two places on the board. */
function midOf(a: { x: number; y: number }, b: { x: number; y: number }): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** The frame that holds the gunner and the target, with room round both. */
function gunRun(): FocusTarget {
  const a = fleetAt(FLEET_GUNNER, 0), b = fleetAt(FLEET_TARGET, 0);
  return around(midOf(a, b), Math.abs(b.x - a.x) + 160, Math.abs(b.y - a.y) + 120);
}

/** The mean centre of a body of cells, in world px. */
function meanOf(cells: readonly number[]): { x: number; y: number } {
  let x = 0, y = 0;
  for (const cell of cells) {
    const p = centreOf(cell);
    x += p.x;
    y += p.y;
  }
  return { x: x / cells.length, y: y / cells.length };
}

/**
 * Where the camera looks for one focus.
 *
 * The chart takes a closer view than the whole board, because most pieces
 * draw above a zoom of 0.5 or 0.75 (`chart.toml`), and the whole board on a
 * telephone is under that.
 */
export function focusTarget(focus: PreviewFocus): FocusTarget {
  switch (focus) {
    case 'torpedo': return gunRun();
    // The detonation look (R2079) lands on the target. The widest splash of
    // the three rounds is 20 px, so the frame holds the hull and the look.
    case 'detonation': return around(fleetAt(FLEET_TARGET, 0), 160, 110);
    case 'damage': return around(fleetAt(FLEET_TARGET, 0), 240, 170);
    // The six gun looks (R1007). A shot look runs the whole way from the
    // gunner to the target, so its frame holds both hulls; a hit look and the
    // final blow land on the target, so their frame is the target's own with
    // room for the mark round it.
    case 'shotMelee': case 'shotArtillery': case 'shotSentinel': return gunRun();
    case 'hitShield': case 'hitHull': case 'hitSink':
      return around(fleetAt(FLEET_TARGET, 0), 200, 140);
    // The four faction marks (R1568) stand on the rival, faction 2, because
    // the viewer's own faction wears none: its hold with the charge beside
    // it, its contact with the pip astern, and the water between the two
    // claims, where the viewer's solid border faces the rival's dashes.
    case 'factionSite': return around(centreOf(RIVAL_HOLD_CELL), 200, 140);
    case 'factionHull': return around(fleetAt(FLEET_TARGET, 0), 150, 105);
    case 'factionEdge':
      return around(midOf(centreOf(cellOf(7, 6)), centreOf(cellOf(11, 6))), 260, 180);
    case 'hold': case 'ownHold': return around(centreOf(HOLD_CELL), 200, 140);
    case 'squadHold': return around(centreOf(MATE_HOLD_CELL), 200, 140);
    // A MARKS option that changes the hull marks and the hold marks (R1915):
    // the own hold and the two own hulls beside it, in one frame.
    case 'holdHulls': return holdHullsFrame();
    case 'ship': case 'ownFleet': return around(fleetAt(FLEET_PATROL, 0), 150, 105);
    case 'squadFleet': return around(fleetAt(FLEET_MATE, 0), 150, 105);
    // The beam runs from the Tender under way to its patient, so the frame
    // must hold both ends of it.
    case 'beam': return around(midOf(fleetAt(FLEET_TENDER, 0), fleetAt(FLEET_PATROL, 0)), 200, 140);
    // The aura is a circle of `PREVIEW_AURA_PX`, and the frame keeps a
    // margin round it, so the outer ring never stands on the edge.
    case 'aura':
      return around(fleetAt(FLEET_ANCHOR, 0),
        2 * PREVIEW_AURA_PX + 100, 2 * PREVIEW_AURA_PX + 60);
    // One own readout and one contact readout in one frame.
    case 'shield':
      return around(midOf(fleetAt(FLEET_PATROL, 0), fleetAt(FLEET_CONTACT, 0)), 170, 115);
    case 'digits': return around(fleetAt(FLEET_PATROL, 0), 110, 78);
    // The stocked deposit and the empty one, so a gauge shows both ends.
    case 'deposit':
      return around(midOf(centreOf(DEPOSIT_CELL), centreOf(EMPTY_DEPOSIT_CELL)), 180, 125);
    // The quiet strategic object and the contested one, for the same reason
    // as the deposit pair (R1104): one field names the whole set, so the
    // frame must hold the mark and its contested tell together.
    case 'structure':
      return around(midOf(centreOf(CP_QUIET_CELL), centreOf(CP_CONTESTED_CELL)), 180, 125);
    // The Dredger on its field (R1120). The look reaches r 26 at its widest,
    // so the frame holds the hull, the diamond and the ring round both.
    case 'gather': return around(fleetAt(FLEET_DREDGER, 0), 140, 100);
    // The Beacon and its buoy (R1125). The widest look reaches r38, and the
    // buoy and its numerals stand right of the hull.
    case 'anchoring': return around(fleetAt(FLEET_BEACON, 0), 140, 100);
    // The Sapper part (R2657): the Sapper and the objects round it, and the
    // detonation on the rival target.
    case 'seaMine': case 'deployable': case 'seaMineLay': case 'deployableLay':
    case 'seaMineCollect': case 'deployableCollect':
      return around(SAPPER_AT, 170, 120);
    // The staged SEARCH (R2833): the hull and the ring of the take, with
    // room under it for the stow display.
    case 'cacheWork': return around(SAPPER_AT, 140, 100);
    case 'seaMineDetonation': return around(fleetAt(FLEET_TARGET, 0), 140, 100);
    // The Sentinel that holds fire (R1439). The widest look reaches r26, and
    // the legend of `restricted-line` stands 33 px under a selected hull.
    case 'holdFire': return around(fleetAt(FLEET_WARD, 0), 140, 100);
    // The Lancer with tubes (R1474). The widest look, `round-label`, runs
    // some 75 px right of the hull, and the tag stands 23 px under it.
    case 'reload': return around({ x: fleetAt(FLEET_TUBES, 0).x + 30, y: fleetAt(FLEET_TUBES, 0).y }, 200, 120);
    // The staged map markers (R1291): both rows, and room over them for a
    // pennant's staff and a flare's column, and under them for the names.
    case 'mapMarker': return markerFrame();
    // The staged points of interest (R2714): the five, and room round them
    // for the words of a set and the reach of the lair.
    case 'poi': return poiFrame();
    case 'chart': return around({ x: PREVIEW_W / 2, y: PREVIEW_H / 2 }, 440, 300);
    // The current field (R2716): the staged gyre over the open water.
    case 'currents':
    case 'currentsHeat': return around(centreOf(GYRE_CELL), 440, 300);
    // The staged band field (R2726): the own hold on the shelf and the three
    // band edges east of it.
    case 'depth': return around({ x: centreOf(HOLD_CELL).x + 1.5 * DEPTH_STEP_PX, y: centreOf(HOLD_CELL).y }, 440, 300);
    // The terrain focus frames the ridge AND the trench together (R828):
    // a value changes both, and the two stand at opposite corners of the
    // board, so the camera looks at the point between them.
    case 'terrain': return around(midOf(meanOf(RIDGE_CELLS), meanOf(TRENCH_CELLS)), 440, 360);
    // The three effect foci (R908). An effect is a slow animation around
    // one object, so each focus frames the object that wears it: the
    // patrol, the round in flight and the viewer's own hold. The frames are
    // wider than the mark frames, because an effect draws outside the mark.
    case 'effectFleet': return around(fleetAt(FLEET_PATROL, 0), 220, 150);
    case 'effectTorpedo': return gunRun();
    case 'effectHold': return around(centreOf(HOLD_CELL), 260, 180);
    // The key is a card in the corner of the frame, over the whole board.
    case 'spawn': case 'territory': case 'factionKey':
    case 'notices': case 'dock': case 'banner': case 'ui':
      return WHOLE;
  }
}

/** The box of a set of points, in world px. */
function boxOf(pts: readonly { x: number; y: number }[]): { x0: number; x1: number; y0: number; y1: number } {
  return {
    x0: Math.min(...pts.map((p) => p.x)), x1: Math.max(...pts.map((p) => p.x)),
    y0: Math.min(...pts.map((p) => p.y)), y1: Math.max(...pts.map((p) => p.y)),
  };
}

/**
 * The frame of the staged map markers: the box of their cells, grown by the
 * room a flare column takes over a cell and a name takes under it and
 * beside it, in world px.
 */
function markerFrame(): FocusTarget {
  const { x0, x1, y0, y1 } = boxOf([...MARKER_FACTION_CELLS, ...MARKER_SQUAD_CELLS].map(centreOf));
  const side = 40, over = 50, under = 30;
  return {
    x: (x0 + x1) / 2, y: (y0 - over + y1 + under) / 2,
    spanX: x1 - x0 + 2 * side, spanY: y1 - y0 + over + under,
  };
}

/** The frame of the staged points of interest: the box of their cells,
 *  grown by the room a name takes beside a mark, in world px. */
function poiFrame(): FocusTarget {
  const { x0, x1, y0, y1 } = boxOf(POI_CELLS.map((p) => centreOf(p.cell)));
  const side = 50, over = 30, under = 30;
  return {
    x: (x0 + x1) / 2, y: (y0 - over + y1 + under) / 2,
    spanX: x1 - x0 + 2 * side, spanY: y1 - y0 + over + under,
  };
}

/**
 * The target of the close-up of the MODDING column (R1914): the same
 * centre, with each span divided by `k`, so the camera comes `k` times
 * nearer than the focus's own frame, inside the camera's zoom range.
 */
export function closeTarget(t: FocusTarget, k: number): FocusTarget {
  const z = Math.max(1, k);
  return { x: t.x, y: t.y, spanX: t.spanX / z, spanY: t.spanY / z };
}

/**
 * The frame of the own hold and the two own hulls beside it, the Sentinel
 * and the set Tender (R1915): the box of the three, grown by the room a
 * hold mark takes round its cell and a hull mark round its hull, in world px.
 */
function holdHullsFrame(): FocusTarget {
  const { x0, x1, y0, y1 } = boxOf([centreOf(HOLD_CELL), fleetAt(FLEET_WARD, 0), fleetAt(FLEET_ANCHOR, 0)]);
  const side = 90, edge = 65;
  return { x: (x0 + x1) / 2, y: (y0 + y1) / 2, spanX: x1 - x0 + 2 * side, spanY: y1 - y0 + 2 * edge };
}

/** The camera: the view offset in CSS px and the zoom. */
export interface ViewPose {
  x: number;
  y: number;
  zoom: number;
}

/**
 * The pose that shows a target on a surface of `w` × `h` CSS px: the zoom
 * that fits both spans, inside the camera's own zoom range, and the offset
 * that puts the centre in the middle.
 */
export function poseFor(t: FocusTarget, w: number, h: number): ViewPose {
  const fit = Math.min(w / Math.max(1, t.spanX), h / Math.max(1, t.spanY));
  const zoom = Math.min(CAMERA_MAX_ZOOM, Math.max(CAMERA_MIN_ZOOM, fit));
  return { zoom, x: w / 2 - t.x * zoom, y: h / 2 - t.y * zoom };
}
